// "Inspired" by Bootstrap 5 util/scrollbar.js
// https://github.com/twbs/bootstrap/blob/fee563d1fca2acea0bde65ab382b4b79f93f600d/js/src/util/scrollbar.js

interface Props {
    [name: string]: any,
}

export class ScrollBar {
    static #props = new WeakMap<HTMLElement, Props>();

    static #SELECTOR_PATCH_PADDING = '.navbar, .navbar__menu';

    static #PROPERTY_OVERFLOW = 'overflow';
    static #PROPERTY_PADDING = 'padding-right';

    static #VALUE_HIDDEN = 'hidden';

    static #element = document.body;

    static #hidden = false;

    private constructor() {
    }

    static #getWidth(): number {
        return Math.abs(window.innerWidth - document.documentElement.clientWidth);
    }

    static hide(): void {
        if (this.#hidden) {
            return;
        }

        const width = this.#getWidth();

        this.#disableOverflow();
        this.#patchPropertyValue(this.#element, this.#PROPERTY_PADDING, (value) => value + width);
        this.#patchPropertyValue(this.#SELECTOR_PATCH_PADDING, this.#PROPERTY_PADDING, (value) => value + width);

        this.#hidden = true;
    }

    static reset(): void {
        if (!this.#hidden) {
            return;
        }

        this.#resetPropertyValue(this.#element, this.#PROPERTY_OVERFLOW);
        this.#resetPropertyValue(this.#element, this.#PROPERTY_PADDING);
        this.#resetPropertyValue(this.#SELECTOR_PATCH_PADDING, this.#PROPERTY_PADDING);

        this.#hidden = false;
    }

    static #savePropertyValue(element: HTMLElement, property: string): void {
        this.#getProps(element)[property] = element.style.getPropertyValue(property);
    }

    static #disableOverflow(): void {
        this.#savePropertyValue(this.#element, this.#PROPERTY_OVERFLOW);
        this.#element.style.setProperty(this.#PROPERTY_OVERFLOW, this.#VALUE_HIDDEN);
    }

    static #patchPropertyValue(element: HTMLElement, property: string, callback: (value: number) => number): void;
    static #patchPropertyValue(selector: string, property: string, callback: (value: number) => number): void;
    static #patchPropertyValue(element: HTMLElement | string, property: string, callback: (value: number) => number): void {
        const scrollbarWidth = this.#getWidth();
        const windowWidth = window.innerWidth;

        (element instanceof HTMLElement ? [element] : Array.from(document.querySelectorAll<HTMLElement>(element)))
            .forEach((element) => {
                if (windowWidth > element.clientWidth + scrollbarWidth) {
                    return;
                }

                this.#savePropertyValue(element, property);

                const value = window.getComputedStyle(element).getPropertyValue(property);
                element.style.setProperty(property, `${callback(Number.parseFloat(value))}px`);
            });
    }

    static #resetPropertyValue(element: HTMLElement, property: string): void;
    static #resetPropertyValue(selector: string, property: string): void;
    static #resetPropertyValue(element: HTMLElement | string, property: string): void {
        (element instanceof HTMLElement ? [element] : Array.from(document.querySelectorAll<HTMLElement>(element)))
            .forEach((element) => {
                const value = this.#getProps(element)[property];

                if (value === null) {
                    element.style.removeProperty(property);

                    return;
                }

                this.#removeProp(element, property);
                element.style.setProperty(property, value);
            });
    }

    static #getProps(element: HTMLElement): Props {
        if (!this.#props.has(element)) {
            this.#props.set(element, {});
        }

        return this.#props.get(element);
    }

    static #removeProp(element: HTMLElement, property: string): void {
        const { [property]: x, ...props } = this.#getProps(element);

        this.#props.set(element, props);
    }
}
